import { AssetSvg } from '@/components/foundation/Svg'
import { classes } from '@lc/libreact'
import { safeProp } from '@lc/libutils'
import { useId, useState, type ReactNode } from 'react'
import { BlockContainer } from './BlockContainer'
import styles from './Collapse.scss'

type CollapseProps = {
	children: ReactNode
	className?: string
	pictoUrl?: string
	title?: string
	level?: 'primary' | 'secondary' | 'tertiary'
	open?: boolean
	listPois?: boolean
}

export function ControlledCollapse({
	children,
	className,
	pictoUrl,
	level = 'primary',
	title,
	open = false,
	setOpen,
	listPois = false,
}: CollapseProps & {
	open: boolean
	setOpen?: (next: boolean) => void
}) {
	const id = useId()

	return (
		<div
			className={classes(
				styles.collapse,
				[styles.primaryCollapse, level === 'primary'],
				[styles.secondaryCollapse, level === 'secondary'],
				[styles.tertiaryCollapse, level === 'tertiary'],
				[styles.open, open],
				className,
			)}
		>
			<button
				type="button"
				className={styles.header}
				onClick={() => setOpen?.(!open)}
				aria-expanded={open}
				aria-controls={id}
			>
				{pictoUrl && (
					<AssetSvg
						className={classes(
							styles.pictoCollapse,
							styles[safeProp(level)],
						)}
						name={pictoUrl}
					/>
				)}
				<h3 className={styles.title}>{title}</h3>
				<AssetSvg
					className={classes(
						styles.pictoPlus,
						styles[safeProp(level)],
					)}
					name={open ? 'assets/Minus.svg' : 'assets/Plus.svg'}
					ariaLabel={open ? 'Fermer' : 'Ouvrir'}
					ariaHidden={false}
				/>
			</button>
			{open && (
				<BlockContainer
					className={classes(
						[styles.childrenContainer, !listPois],
						[styles.childrenListPoisContainer, listPois],
					)}
					id={id}
				>
					{children}
				</BlockContainer>
			)}
		</div>
	)
}

export function Collapse({
	open: defaultOpen = false,
	...props
}: CollapseProps) {
	const [open, setOpen] = useState<boolean>(defaultOpen ?? false)
	return <ControlledCollapse {...props} open={open} setOpen={setOpen} />
}
